import Cookies from "js-cookie";
import { Dispatch, SetStateAction } from "react";

import { OtpData, RegisterFormType, SmsStatus, SmsVerifyStatus } from "../types/register";
import authUtils from "../utils/auth.utils";
import { postDontHandleError } from "./fetch.core";

async function register(data: RegisterFormType) {
  try {
    // Clean up the data
    if (!data.email) {
      delete (data as any).email;
    }

    // Make sure we're sending the right data format
    const registerData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      countryCode: data.countryCode,
      mobileNumber: data.mobileNumber,
      password: data.password,
      confirmPassword: data.confirmPassword,
    };

    // Let's try with a simpler approach - directly use fetch instead
    const response = await fetch(`${process.env.NEXT_PUBLIC_URL_ROOT}/public/user/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "shop-id": process.env.NEXT_PUBLIC_SHOP_ID || "",
        locale: typeof window !== "undefined" && window.location.pathname.startsWith("/zh") ? "zh" : "en",
      },
      body: JSON.stringify(registerData),
    });

    // Handle errors
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Unknown Error");
    }

    // Parse the successful response
    const responseData = await response.json();
    return responseData;
  } catch (error: any) {
    console.error("Registration error:", error);

    // Specifically handle known error messages
    if (
      error.message === "userAlreadyExists" ||
      error.message === "mobileAlreadyExists" ||
      error.message === "emailAlreadyExists"
    ) {
      throw error;
    }

    // For any other errors
    throw new Error("Unknown Error");
  }
}

// The rest of your code stays the same
export interface VerifyOtpRst {
  message: string;
  resetPwdUrl?: string;
}

async function verifyOtpResetPwd(data: OtpData, otp: string, setVerifyStatus: Dispatch<SetStateAction<SmsVerifyStatus>>) {
  const res = await postDontHandleError<VerifyOtpRst>("/public/user/verifySmsOtpResetPwd", {
    countryCode: data.countryCode,
    mobileNumber: data.mobileNumber,
    otp,
  });

  if (res.status !== 200) {
    setVerifyStatus(res.data!.message as any);
    return res.data;
  }

  setVerifyStatus("success");
  return res.data;
}

type VerifySmsOtpInput =
  | {
      mobileNumber: number;
      countryCode: number;
      otp: string;
    }
  | {
      userId: string;
      otp: string;
    };

async function verifyOtp(input: VerifySmsOtpInput, setVerifyStatus: Dispatch<SetStateAction<SmsVerifyStatus>>) {
  setVerifyStatus("verifying");

  const res = await postDontHandleError<VerifyOtpRst>("/public/user/verifySmsOtp", input);

  if (res.status !== 200) {
    setVerifyStatus(res.data!.message as any);
    return;
  }

  setVerifyStatus("success");

  return res.data;
}

async function updateTelVerifySmsOtp(otp: string, setVerifyStatus: Dispatch<SetStateAction<SmsVerifyStatus>>, tempAuth = false) {
  setVerifyStatus("verifying");

  const options = { useToken: false, useShopId: true, locale: undefined };
  // Get the token and create proper headers object
  const accessToken = authUtils.getAccessToken(tempAuth);

  // Handle different token formats and null case
  let headers: { [key: string]: string } | undefined = undefined;
  if (accessToken) {
    if (typeof accessToken === "string") {
      headers = { "x-access-token": accessToken };
    } else {
      headers = accessToken; // Already in the right format
    }
  }

  const res = await postDontHandleError<VerifyOtpRst>("/public/user/updateTelVerifySmsOtp", { otp }, options, headers);

  if (res.status !== 200) {
    setVerifyStatus(res.data!.message as any);
    return;
  }

  setVerifyStatus("success");

  return res.data;
}

type ResendOtpInput =
  | {
      mobileNumber: number;
      countryCode: number;
    }
  | {
      userId: string;
    };

async function resendOtp(data: ResendOtpInput, setSmsStatus: Dispatch<SetStateAction<SmsStatus | null>>, updating?: boolean) {
  setSmsStatus("sending");

  const resendUrl = updating ? "/public/user/updateTelResendOtp" : "/public/user/resendOtp";

  try {
    const res = await postDontHandleError<{ message: string }>(resendUrl, data);
    if (res.status !== 200) {
      if (res.data!.message === "tooOften") {
        setSmsStatus("tooOften");
      } else {
        setSmsStatus("error");
      }
      return false;
    }

    setSmsStatus(null);
    return true;
  } catch (error) {
    console.error("Error sending OTP:", error);
    setSmsStatus("error");
    return false;
  }
}

const obj = { register, verifyOtp, verifyOtpResetPwd, resendOtp, updateTelVerifySmsOtp };

export default obj;
